import PropTypes from 'prop-types';
import { campaignActions } from 'core/Campaign';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import {
    ButtonFooter,
    Container,
    DateSelect,
    DragAndDrop,
    ExpandButton,
    FormGroup,
    Loader,
    Modal
} from 'mol-ui';
import {
    ErrorMessage,
    FileList
} from 'views/components';
import React, { useEffect, useState } from 'react';
import {
    dateToObject,
    objectToDate,
    validationSchema
} from 'shared/utils';
import {
    useDispatch,
    useErrors,
    useFileUpload,
    useObjectState,
    useSelector
} from 'hooks';

const DEFAULT_TRAINING = {
    date: dateToObject(moment()),
    file_id: null
};

const TrainingModal = ({
    active,
    id,
    participantId,
    onToggle
}) => {

    const dispatch = useDispatch();
    const { t } = useTranslation();

    const {
        processing,
        training,
        trainingLoading
    } = useSelector('campaign');

    const [currentData, setCurrentData, setCurrentDataValue] = useObjectState({ ...DEFAULT_TRAINING });
    const [currentFile, setCurrentFile] = useState(null);
    const { clearError, clearErrors, errors, setErrors } = useErrors();
    const [uploadFile, loadingFile] = useFileUpload();

    useEffect(() => {
        setCurrentData({ ...DEFAULT_TRAINING });
        clearErrors();
        if (!id || id === 0) {
            dispatch(campaignActions.setCampaignTraining(null));
            return;
        }
        dispatch(campaignActions.getTraining(id));
    }, [id]);

    useEffect(() => {
        if (isEmpty(training)) {
            setCurrentData({ ...DEFAULT_TRAINING });
            setCurrentFile(null);
        } else {
            setCurrentData({
                ...training,
                date: dateToObject(moment(training?.date)),
                file_id: training?.file?.id
            });
            setCurrentFile(training?.file);
        }
    }, [training]);

    const handleFileUpload = async file => {
        clearError('file_id');
        const { data } = await uploadFile(file);
        setCurrentFile(data);
        setCurrentDataValue('file_id', data?.id);
    };

    const handleSave = () => {
        if (!validate()) return;
        dispatch(campaignActions.upsertTraining({
            ...currentData,
            date: objectToDate(currentData?.date).format('YYYY-MM-DD'),
            participant_id: participantId,
            training_id: currentData?.id
        }));
    };

    const validate = () => {
        const { file_id } = currentData;
        const { errors, isValid } = validationSchema({
            file_id: [!!file_id, t('El certificado es requerido')]
        });
        setErrors(errors);
        return isValid;
    };

    return (
        <Modal active={ active } width="360px">
            <Container width="100%">
                <Loader active={ trainingLoading } />
                {
                    !trainingLoading &&
                    <>
                        <FormGroup label={ t('Certificado') } margin="0px 0px 20px 0px">
                            <DragAndDrop
                                error={ !!errors?.file_id }
                                loading={ loadingFile }
                                onFileChange={ handleFileUpload }
                            />
                            <ErrorMessage relative error={ errors?.file_id } margin="5px 0px 0px 0px" />
                        </FormGroup>
                        {
                            !!currentFile &&
                            <Container margin="0px 0px 20px 0px" width="100%">
                                <FileList files={ [currentFile] } />
                            </Container>
                        }
                        <FormGroup label={ t('Fecha') } margin="0px 0px 10px 0px">
                            <DateSelect
                                value={ currentData?.date }
                                onChange={ value => setCurrentDataValue('date', value) }
                            />
                        </FormGroup>
                    </>
                }
                <ButtonFooter margin="20px 0px 10px 0px">
                    <ExpandButton
                        danger
                        caption={ t('Volver') }
                        size={ 100 }
                        onClick={ onToggle }
                    />
                    <ExpandButton
                        caption={ t('Guardar') }
                        loading={ processing }
                        size={ 100 }
                        onClick={ handleSave }
                    />
                </ButtonFooter>
            </Container>
        </Modal>
    );
};

TrainingModal.propTypes = {
    active: PropTypes.bool,
    id: PropTypes.number,
    onToggle: PropTypes.func,
    participantId: PropTypes.number
};

export default TrainingModal;
